import { memo, RefObject, useLayoutEffect, useRef } from 'react'
import { TDataRef } from '@/services/data/data.types.ts'
import { ILineChartWidget } from '../../dashboards.types'
import css from './lineChart.module.css'
import CLineChart from './core/CLineChart'
import useSkipFirstRenderEffect from '../../../../shared/hooks/useSkipFirstRenderEffect'
import chartsUtils from '../lib/charts.utils'
import { useRecoilValue } from 'recoil'
import usePlayer from '@/pages/Dashboards/services/player/usePlayer.ts'
import parametersSelectors from '@/pages/Parameters/parameters.selectors.ts'
import cn from '@/utils/cn.ts'

interface IProps {
  dataRef: RefObject<TDataRef>
  widget: ILineChartWidget
}

const LineChart = (props: IProps) => {
  const canvasRef = useRef<HTMLCanvasElement>()
  const wrapperRef = useRef<HTMLDivElement>()
  const tooltipRef = useRef<HTMLDivElement>()
  const chartRef = useRef<CLineChart>()
  const player = usePlayer()

  const parametersMap = useRecoilValue(parametersSelectors.getParametersMap)
  const unitPrefixesMap = useRecoilValue(parametersSelectors.getUnitsPrefixesMap)
  const unitsMap = useRecoilValue(parametersSelectors.getUnitsMap)

  useLayoutEffect(() => {
    const rect = wrapperRef.current.getBoundingClientRect().toJSON()
    chartsUtils.scaleCanvas(rect.width, rect.height, canvasRef)

    chartRef.current = new CLineChart({
      canvasRef: canvasRef,
      dataRef: props.dataRef,
      wrapperRef: wrapperRef,
      tooltipRef: tooltipRef,
      frame: player.frame,
      live: player.live,
      config: props.widget.configuration.settings,
      boundaries: rect,
      tsTo: player.tsTo,
      tsFrom: player.tsFrom,
      parametersMap,
      unitPrefixesMap,
      unitsMap,
      keepLastValue: true,
    })

    const resizeObserverCallback: ResizeObserverCallback = (e) => {
      const updatedRect = e[0].target.getBoundingClientRect()
      chartsUtils.scaleCanvas(updatedRect.width, updatedRect.height, canvasRef)

      chartRef.current.updateBoundaries(updatedRect)
    }

    const resizeObserver = new ResizeObserver(resizeObserverCallback)
    resizeObserver.observe(wrapperRef.current)

    return () => {
      chartRef.current.chart.destroy()
      resizeObserver.disconnect()
    }
  }, [])

  useSkipFirstRenderEffect(() => {
    chartRef.current.setIsLive(player.live)
    chartRef.current.updateTimeInterval(player.tsFrom, player.tsTo)
    chartRef.current.updateFrame(player.frame)
  }, [player])

  useSkipFirstRenderEffect(() => {
    if (props.widget.configuration.settings) {
      chartRef.current.updateConf(props.widget.configuration.settings)
    }
  }, [props.widget.configuration.settings])

  return (
    <div className={css.wrapper} ref={wrapperRef}>
      <canvas className={css.canvas} ref={canvasRef} />
      <div className={css.tooltipLine} ref={tooltipRef}>
        <div className={css.tooltip}>
          <div className={cn('text-nm text-white', css.tooltipTime)} />
          <div className={cn('text-nm text-white', css.tooltipValues)} />
        </div>
      </div>
    </div>
  )
}

export default memo(LineChart)
